import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Target, Eye, Heart, Award, Users, TrendingUp, CheckCircle, Shield, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const About = ({ onOpenPopup }) => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>About Trademark Inspire - Leading Trademark & IP Protection Services | Our Story & Mission</title>
        <meta name="description" content="Learn about Trademark Inspire - your trusted partner in intellectual property protection since inception. 10,000+ successful trademark registrations, expert legal team, and commitment to excellence in brand protection." />
        <meta name="keywords" content="trademark company, IP protection services, trademark experts, about us, brand protection company, trademark law firm, intellectual property attorneys" />
      </Helmet>
      {/* Hero Section - Service Page Style */}
      <section className="relative bg-gradient-to-br from-[#093336] via-[#0a4548] to-[#093336] text-white py-20 md:py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0ZWFtfGVufDB8fHx8MTc2NDAyNDY3MXww&ixlib=rb-4.1.0&q=85)', 
            backgroundSize: 'cover', 
            backgroundPosition: 'center' 
          }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              About Trademark Inspire
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-100">
              Your trusted partner in protecting intellectual property and building lasting business success since our inception
            </p>
            <Button
              onClick={onOpenPopup}
              className="bg-[#F77F00] hover:bg-[#e57300] text-white px-8 py-6 text-lg font-semibold"
            >
              Get Started Today <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="flex items-center justify-center">
                <img 
                  src="https://images.unsplash.com/photo-1568992687947-868a62a9f521?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0ZWFtfGVufDB8fHx8MTc2NDAyNDY3MXww&ixlib=rb-4.1.0&q=85" 
                  alt="Our Team" 
                  className="rounded-2xl shadow-2xl w-full h-auto max-h-[350px] object-cover"
                />
              </div>
              <div className="flex flex-col justify-center">
                <h2 className="text-3xl font-bold text-[#093336] mb-6">Who We Are</h2>
                <p className="text-gray-700 text-lg mb-4">
                  Trademark Inspire is a leading provider of trademark and intellectual property services in the United States. We specialize in helping businesses of all sizes protect their most valuable assets - their brands, innovations, and creative works.
                </p>
                <p className="text-gray-700 text-lg mb-4">
                  Our team of experienced professionals combines deep legal expertise with a client-first approach, ensuring that every trademark application receives the attention and care it deserves.
                </p>
                <p className="text-gray-700 text-lg">
                  We believe that protecting your intellectual property shouldn't be complicated or overwhelming. That's why we've streamlined our processes to make trademark registration accessible, affordable, and stress-free.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#093336] text-white rounded-xl flex items-center justify-center mb-6">
                  <Target size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#093336] mb-4">Our Mission</h3>
                <p className="text-gray-700">
                  To empower businesses by providing expert trademark and intellectual property services that protect their brand identity and foster long-term growth and success.
                </p>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#093336] text-white rounded-xl flex items-center justify-center mb-6">
                  <Eye size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#093336] mb-4">Our Vision</h3>
                <p className="text-gray-700">
                  To be the most trusted and innovative trademark services provider, recognized for our commitment to excellence, integrity, and client success in protecting intellectual property rights.
                </p>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 bg-[#093336] text-white rounded-xl flex items-center justify-center mb-6">
                  <Heart size={32} />
                </div>
                <h3 className="text-2xl font-bold text-[#093336] mb-4">Our Values</h3>
                <p className="text-gray-700">
                  Integrity, excellence, client-focused service, innovation, and transparency guide everything we do. We're committed to delivering results that exceed expectations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Commitment to Excellence Section - Image on Right Aligned with Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="flex flex-col justify-center">
                <h2 className="text-3xl md:text-4xl font-bold text-[#093336] mb-6">
                  Our Commitment to Excellence
                </h2>
                <p className="text-lg text-gray-700 mb-4">
                  At Trademark Inspire, we understand that your brand represents years of hard work, innovation, and dedication. That's why we're committed to providing the highest level of service and expertise in trademark protection.
                </p>
                <p className="text-lg text-gray-700 mb-4">
                  Our team brings together legal expertise, industry knowledge, and a genuine passion for helping businesses succeed. We don't just process applications – we build lasting relationships with our clients.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#F77F00] flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700"><strong>Personalized Service:</strong> Every client receives dedicated attention and customized solutions</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#F77F00] flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700"><strong>Proven Track Record:</strong> 98% success rate with thousands of satisfied clients</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-[#F77F00] flex items-center justify-center flex-shrink-0 mt-1">
                      <CheckCircle size={16} className="text-white" />
                    </div>
                    <p className="text-gray-700"><strong>Comprehensive Support:</strong> From initial search to ongoing protection and monitoring</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-center">
                <img 
                  src="https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjB0ZWFtfGVufDB8fHx8MTc2NDAyNDY3MXww&ixlib=rb-4.1.0&q=85" 
                  alt="Our Expertise" 
                  className="rounded-2xl shadow-2xl w-full h-auto max-h-[380px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#093336] mb-4">Our Approach</h2>
              <p className="text-xl text-gray-600">
                We combine expertise with innovation to deliver exceptional results
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1622675363311-3e1904dc1885?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwzfHxvZmZpY2UlMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc2NDAyNDY3Nnww&ixlib=rb-4.1.0&q=85" 
                  alt="Collaboration" 
                  className="rounded-2xl shadow-2xl w-full"
                />
              </div>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#F77F00] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#093336] mb-2">Expert Guidance</h3>
                    <p className="text-gray-700">Our experienced team provides personalized guidance throughout the entire trademark registration process.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#F77F00] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Users size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#093336] mb-2">Client-First Service</h3>
                    <p className="text-gray-700">We prioritize your needs and work tirelessly to ensure your satisfaction and success at every step.</p>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-[#F77F00] text-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingUp size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#093336] mb-2">Proven Results</h3>
                    <p className="text-gray-700">With a 98% success rate, we deliver results that protect your brand and give you peace of mind.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Work With Us */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#093336] mb-4">Why Work With Us?</h2>
              <p className="text-xl text-gray-600">What sets us apart from other service providers</p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-[#093336] mb-3">Comprehensive Expertise</h3>
                <p className="text-gray-700">From trademarks to patents, copyrights to company formation - we offer a complete suite of services under one roof. Our multidisciplinary team handles all your intellectual property and corporate needs.</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-[#093336] mb-3">Transparent Pricing</h3>
                <p className="text-gray-700">No hidden fees or surprise charges. Our pricing is clear, upfront, and competitive. We believe in honest communication about costs from the very beginning.</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-[#093336] mb-3">Fast Processing</h3>
                <p className="text-gray-700">We understand time is money. Our streamlined processes ensure quick turnaround without compromising quality. Most applications are filed within 3-5 business days.</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-[#093336] mb-3">Dedicated Support</h3>
                <p className="text-gray-700">Your dedicated case manager is always available to answer questions and provide updates on your application. We're here for you every step of the way.</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-[#093336] mb-3">Industry Experience</h3>
                <p className="text-gray-700">Our team brings decades of combined experience in trademark law, intellectual property, and corporate formation. We've seen it all and know how to handle any situation.</p>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
                <h3 className="text-xl font-bold text-[#093336] mb-3">Client-First Philosophy</h3>
                <p className="text-gray-700">Your success is our success. We prioritize your needs, communicate clearly, and work tirelessly to achieve the best possible outcomes for your business.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#093336] mb-4">Areas of Expertise</h2>
              <p className="text-xl text-gray-600">Comprehensive legal and corporate services for every stage of your business</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-[#093336] text-white rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <Shield size={40} />
                </div>
                <h3 className="text-xl font-bold text-[#093336] mb-3">Trademark Protection</h3>
                <p className="text-gray-700">Complete trademark services from search to registration, monitoring, and enforcement. Protect your brand identity nationwide.</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-[#093336] text-white rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <Users size={40} />
                </div>
                <h3 className="text-xl font-bold text-[#093336] mb-3">Business Formation</h3>
                <p className="text-gray-700">LLC, Corporation, S-Corp, and Non-Profit formation services. Choose the right structure for your business goals.</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-[#093336] text-white rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <Award size={40} />
                </div>
                <h3 className="text-xl font-bold text-[#093336] mb-3">Copyright & Patents</h3>
                <p className="text-gray-700">Protect your creative works and inventions with copyright registration and patent services.</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-[#093336] text-white rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <Target size={40} />
                </div>
                <h3 className="text-xl font-bold text-[#093336] mb-3">Legal Counsel</h3>
                <p className="text-gray-700">Expert legal advice for business immigration, due diligence, commercial law, and data protection compliance.</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-[#093336] text-white rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <TrendingUp size={40} />
                </div>
                <h3 className="text-xl font-bold text-[#093336] mb-3">Corporate Services</h3>
                <p className="text-gray-700">Comprehensive corporate support including bookkeeping, tax advisory, share transfers, and ongoing compliance.</p>
              </div>
              
              <div className="text-center">
                <div className="w-20 h-20 bg-[#093336] text-white rounded-2xl flex items-center justify-center mb-4 mx-auto">
                  <Eye size={40} />
                </div>
                <h3 className="text-xl font-bold text-[#093336] mb-3">Strategic Planning</h3>
                <p className="text-gray-700">CFO services, financial planning, and strategic business advisory to help your company grow and thrive.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Numbers Section */}
      <section className="py-20 bg-gradient-to-br from-[#093336] to-[#0a4548] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-4">Success in Numbers</h2>
              <p className="text-xl text-gray-200">The metrics that matter to our clients</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-5xl font-bold text-[#F77F00] mb-2">10,000+</div>
                <div className="text-lg text-gray-200">Trademarks Registered</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#F77F00] mb-2">98%</div>
                <div className="text-lg text-gray-200">USPTO Approval Rate</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#F77F00] mb-2">5,000+</div>
                <div className="text-lg text-gray-200">Satisfied Clients</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-bold text-[#F77F00] mb-2">24/7</div>
                <div className="text-lg text-gray-200">Expert Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment to Quality Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#093336] mb-6">Our Commitment to Quality</h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8">
                At Trademark Inspire, quality is not just a buzzword—it's the foundation of everything we do. Every application we file, every document we prepare, and every interaction we have with clients is guided by our unwavering commitment to excellence.
              </p>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-[#093336] mb-4">Rigorous Quality Control</h3>
                <p className="text-gray-700 leading-relaxed">
                  Every trademark application undergoes multiple levels of review before filing. Our quality control process ensures accuracy, completeness, and compliance with USPTO requirements. We catch potential issues before they become problems, saving you time and money.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-[#093336] mb-4">Continuous Education</h3>
                <p className="text-gray-700 leading-relaxed">
                  Trademark law and intellectual property regulations are constantly evolving. Our team stays current with the latest changes through ongoing education, professional development, and active participation in legal communities. When you work with us, you benefit from the most up-to-date knowledge and strategies.
                </p>
              </div>
              
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-bold text-[#093336] mb-4">Client Satisfaction Focus</h3>
                <p className="text-gray-700 leading-relaxed">
                  We measure our success by your satisfaction. That's why we maintain a 98% client satisfaction rate and receive countless referrals from happy clients. We don't just complete transactions—we build lasting relationships based on trust, transparency, and results.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#093336] via-[#0a4548] to-[#093336] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-100 mb-8 max-w-2xl mx-auto">
            Let us help you protect what matters most - your brand, your innovation, your future.
          </p>
          <Button
            onClick={onOpenPopup}
            className="bg-[#F77F00] hover:bg-[#e57300] text-white px-8 py-6 text-lg font-semibold"
          >
            Contact Us Today
          </Button>
        </div>
      </section>
    </div>
  );
};

export default About;
